import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { randomRange } from "../../../common/common_funcs.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { SceneManager } from "../../../core/scene_manager.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { PhysicsWorld } from "../../../physics/physics_world.js";
import { VfxPhysicsParticleBatch } from "../../core/batches/vfx_physics_particle_batch.js";
import { Vfx3DGlobalAtlas } from "../../core/textures/vfx_3d_global_atlas.js";

type SpillBlob = {
  group: THREE.Group;
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  base_scale: number;
  vx: number;
  vy: number;
  vz: number;
  hit_floor_time: number;
  floor_y: number;
  landing_quaternion: THREE.Quaternion;
  clip_plane: THREE.Plane;
  dissolve_uniform: { value: number };
};

export class VfxJamSpillTask implements IVfxTask, IController
{
  private static readonly DURATION: number = 1;
  private static readonly MIN_BLOBS: number = 4;
  private static readonly MAX_BLOBS: number = 6;
  private static readonly OFFSET_MIN: number = -0.25;
  private static readonly OFFSET_MAX: number = 0.25;
  private static readonly SCALE_MIN: number = 0.15;
  private static readonly SCALE_MAX: number = 0.3;
  private static readonly DISSOLVE_START: number = 0.6;
  private static readonly DISSOLVE_SOFTNESS: number = 0.1;
  private static readonly NOISE_SCALE: number = 1;

  // --- Floor Splash Constants ---
  private static readonly SPLASH_COUNT              : number = 5;
  private static readonly SPLASH_RADIAL_MIN         : number = 1;
  private static readonly SPLASH_RADIAL_MAX         : number = 4;
  private static readonly SPLASH_UP_MIN             : number = 1;
  private static readonly SPLASH_UP_MAX             : number = 2.5;
  private static readonly SPLASH_GRAVITY            : number = 14;
  private static readonly SPLASH_SCALE_MIN          : number = 0.4;
  private static readonly SPLASH_SCALE_MAX          : number = 0.6;
  private static readonly SPLASH_LIFESPAN_MIN       : number = 0.4;
  private static readonly SPLASH_LIFESPAN_MAX       : number = 0.6;
  private static readonly JAM_INITIAL_DISSOLVE_MIN  : number = 0.2;
  private static readonly JAM_INITIAL_DISSOLVE_MAX  : number = 0.3;
  private static readonly SPLASH_NOISE_RES_ID       : ResourceID = ResourceID.PerlinNoiseTexture;
  private static readonly SPLASH_NOISE_SCALE        : number = 1.5;
  private static readonly SPLASH_DISSOLVE_START     : number = 0.2;
  private static readonly SPLASH_DISSOLVE_SOFTNESS  : number = 0.1;

  private static readonly scratch_splash_pos  : THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_splash_vel  : THREE.Vector3 = new THREE.Vector3();

  private static readonly SQUASH_SPEED: number = 3;
  private static readonly SQUASH_EXPAND_XZ: number = 2;
  private static readonly SQUASH_FLATTEN_Y: number = 0.4;

  private static readonly GRAVITY: number = 20;
  private static readonly SPEED_XZ_MIN: number = 0.5;
  private static readonly SPEED_XZ_MAX: number = 2;
  private static readonly SPEED_Y: number = 0;

  private static readonly SURFACE_MAP: Record<number, ResourceID> = {
    0: ResourceID.JamCherryTexture,
    1: ResourceID.JamPlumTexture,
    2: ResourceID.JamLemonTexture,
    3: ResourceID.JamFigTexture,
  };

  public is_active: boolean = true;
  private blobs: SpillBlob[] = [];
  private start_time: number;
  private last_now: number | null = null;
  private geometry!: THREE.BufferGeometry;
  private half_height: number;
  private height_blocks: number;
  private floor_y: number;
  private rotation: THREE.Quaternion;
  private surface_res_id: ResourceID;

  public static createSpillMaterial(
    map?: THREE.Texture,
    clip_plane?: THREE.Plane,
    dissolve_uniform: { value: number } = { value: 0.0 },
  ): THREE.MeshBasicMaterial
  {
    const material = new THREE.MeshBasicMaterial( {
      map,
      color: 0xffffff,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: true,
      side: THREE.FrontSide,
    } );

    if ( clip_plane )
    {
      material.clippingPlanes = [ clip_plane ];
    }

    let atlas_texture: THREE.Texture | null = null;
    const u_noise_uv = new THREE.Vector4( 0, 0, 1, 1 );
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
      const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.VornoiNoiseTexture );
      u_noise_uv.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
    }
    catch ( _e )
    {
      // Atlas will be populated lazily or available during warmup
    }

    material.onBeforeCompile = ( shader ): void =>
    {
      shader.uniforms.uNoiseTexture = { value: atlas_texture };
      shader.uniforms.uNoiseUv = { value: u_noise_uv };
      shader.uniforms.uDissolveProgress = dissolve_uniform;

      shader.fragmentShader = `
        uniform sampler2D uNoiseTexture;
        uniform vec4 uNoiseUv;
        uniform float uDissolveProgress;
      ` + shader.fragmentShader;

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <alphamap_fragment>',
        `
        #include <alphamap_fragment>
        if ( uDissolveProgress > 0.0 )
        {
          vec2 uvCoord = vec2(0.0);
          #ifdef USE_MAP
            uvCoord = vMapUv;
          #elif defined( USE_UV )
            uvCoord = vUv;
          #endif
          vec2 atlasNoiseUv = mix( uNoiseUv.xy, uNoiseUv.zw, fract( uvCoord * ${VfxJamSpillTask.NOISE_SCALE.toFixed( 1 )} ) );
          vec4 noiseTex = texture2D( uNoiseTexture, atlasNoiseUv );
          float erosionNoise = noiseTex.r;
          float dissolveAlpha = clamp( ( erosionNoise - uDissolveProgress * 1.2 + 0.2 ) / ${VfxJamSpillTask.DISSOLVE_SOFTNESS.toFixed( 2 )}, 0.0, 1.0 );
          diffuseColor.a *= dissolveAlpha;
        }
        `
      );
    };

    return material;
  }

  public static createWarmupProbe(): THREE.Mesh
  {
    const texture = ResourceManager.getTexture( ResourceID.JamCherryTexture ) ?? undefined;
    const clip_plane = new THREE.Plane( new THREE.Vector3( 0, 1, 0 ), 0 );
    const material = this.createSpillMaterial( texture, clip_plane );

    const mesh = new THREE.Mesh( new THREE.BoxGeometry( 0.01, 0.01, 0.01 ), material );
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.renderOrder = 10;

    return mesh;
  }

  constructor(
    position: THREE.Vector3,
    half_height: number,
    height_blocks: number,
    floor_y: number,
    jam_set_index: number,
    rotation?: THREE.Quaternion,
  )
  {
    this.start_time = Vfx3DRuntime.current_time;
    this.half_height = half_height;
    this.height_blocks = height_blocks;
    this.floor_y = floor_y;
    this.rotation = rotation ? rotation.clone() : new THREE.Quaternion();

    this.surface_res_id = VfxJamSpillTask.SURFACE_MAP[jam_set_index] ?? ResourceID.JamCherryTexture;
    const texture = ResourceManager.getTexture( this.surface_res_id ) ?? undefined;

    const mult = 1 + 0.4 * ( this.height_blocks - 1 );
    const blob_count = Math.floor( randomRange( VfxJamSpillTask.MIN_BLOBS, VfxJamSpillTask.MAX_BLOBS ) * mult );

    const model = ResourceManager.getModel( ResourceID.Particle1Model );
    let jam_particle_geom: THREE.BufferGeometry | null = null;
    model.traverse( child =>
    {
      if ( child instanceof THREE.Mesh && !jam_particle_geom )
        jam_particle_geom = child.geometry.clone();
    } );

    if ( !jam_particle_geom )
      throw new Error( "JamParticle model mesh geometry not found" );

    this.geometry = jam_particle_geom;

    for ( let i = 0; i < blob_count; i++ )
    {
      const group = new THREE.Group();
      const clip_plane = new THREE.Plane( new THREE.Vector3( 0, 1, 0 ), -this.floor_y );
      const dissolve_uniform = { value: 0.0 };
      const material = VfxJamSpillTask.createSpillMaterial( texture, clip_plane, dissolve_uniform );

      const mesh = new THREE.Mesh( this.geometry, material );
      mesh.castShadow = false;
      mesh.receiveShadow = true;

      const base_scale = randomRange( VfxJamSpillTask.SCALE_MIN, VfxJamSpillTask.SCALE_MAX );
      mesh.scale.set( base_scale, base_scale, base_scale );

      group.add( mesh );

      const angle = randomRange( 0, Math.PI * 2 );
      const speed_xz = randomRange( VfxJamSpillTask.SPEED_XZ_MIN, VfxJamSpillTask.SPEED_XZ_MAX );
      const local_vx = Math.cos( angle ) * speed_xz;
      const local_vz = Math.sin( angle ) * speed_xz;
      const local_vy = VfxJamSpillTask.SPEED_Y;
      const velocity = new THREE.Vector3( local_vx, local_vy, local_vz ).applyQuaternion( this.rotation );

      const span = this.half_height * 0.75;
      const local_offset = new THREE.Vector3(
        randomRange( VfxJamSpillTask.OFFSET_MIN, VfxJamSpillTask.OFFSET_MAX ),
        randomRange( -span, span ),
        randomRange( VfxJamSpillTask.OFFSET_MIN, VfxJamSpillTask.OFFSET_MAX )
      ).applyQuaternion( this.rotation );
      
      group.position.set( position.x + local_offset.x, position.y + local_offset.y, position.z + local_offset.z );

      SceneManager.world.add( group );

      this.blobs.push( {
        group,
        mesh,
        material,
        base_scale,
        vx: velocity.x,
        vy: velocity.y,
        vz: velocity.z,
        hit_floor_time: -1,
        floor_y: 0,
        landing_quaternion: new THREE.Quaternion(),
        clip_plane,
        dissolve_uniform,
      } );
    }
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
  }

  public stop( behavior?: StopBehavior ): void
  {
    this.is_active = false;
    this.disposeAll();
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    const age = now - this.start_time;
    if ( age < 0 )
      return false;

    const dt = Math.min( 0.1, Math.max( 0, now - ( this.last_now ?? this.start_time ) ) );
    this.last_now = now;

    const progress = age / VfxJamSpillTask.DURATION;

    if ( progress >= 1.0 )
    {
      this.disposeAll();
      return true;
    }
    const dissolve_progress = progress > VfxJamSpillTask.DISSOLVE_START
      ? ( progress - VfxJamSpillTask.DISSOLVE_START ) / ( 1.0 - VfxJamSpillTask.DISSOLVE_START )
      : 0.0;

    const world = PhysicsWorld.getWorld();

    for ( const blob of this.blobs )
    {
      blob.dissolve_uniform.value = dissolve_progress;

      if ( blob.hit_floor_time < 0 )
      {
        blob.vy -= VfxJamSpillTask.GRAVITY * dt;
        blob.group.position.x += blob.vx * dt;
        blob.group.position.y += blob.vy * dt;
        blob.group.position.z += blob.vz * dt;

        let current_floor_y = this.floor_y;
        const ray_origin = { x: blob.group.position.x, y: blob.group.position.y + 0.5, z: blob.group.position.z };
        const ray_dir = { x: 0, y: -1, z: 0 };
        const ray = new RAPIER.Ray( ray_origin, ray_dir );
        
        const hit = world.castRay( ray, 100.0, true, undefined, undefined, undefined, undefined, ( col ) => col.parent()?.isFixed() ?? false );
        
        if ( hit )
        {
          current_floor_y = ray_origin.y - hit.timeOfImpact;
        }

        // Continuously align clipping plane to current ground level so particle clips smoothly as it penetrates ground plane
        blob.clip_plane.constant = -current_floor_y;

        if ( blob.group.position.y <= current_floor_y )
        {
          blob.group.position.y = current_floor_y;
          blob.hit_floor_time = age;
          blob.floor_y = current_floor_y;
          blob.landing_quaternion.copy( blob.mesh.quaternion );

          this.spawnFloorSplash( blob.group.position, current_floor_y, now );
        }
        else
        {
          // Deform while flying: stretch along velocity vector
          const velocity = new THREE.Vector3( blob.vx, blob.vy, blob.vz );
          const speed = velocity.length();
          if ( speed > 0.01 )
          {
            blob.mesh.quaternion.setFromUnitVectors( new THREE.Vector3( 0, 1, 0 ), velocity.normalize() );
          }
          const stretch = 1.0 + speed * 0.05;
          const squash = 1.0 / Math.sqrt( stretch );
          blob.mesh.scale.set( blob.base_scale * squash, blob.base_scale * stretch, blob.base_scale * squash );
        }
      }

      if ( blob.hit_floor_time >= 0 )
      {
        // Grounded update:
        // 1. Position stays fixed AT floor_y
        blob.group.position.y = blob.floor_y;

        // 2. Keep landing 3D orientation (no popping/jumping!)
        blob.mesh.quaternion.copy( blob.landing_quaternion );

        const splat_age = age - blob.hit_floor_time;
        const squash_progress = Math.min( 1.0, splat_age * VfxJamSpillTask.SQUASH_SPEED );

        // 3. Expand XZ scale outwards while flattening Y scale down towards the floor
        const scale_xz = 1.0 + squash_progress * VfxJamSpillTask.SQUASH_EXPAND_XZ;
        const scale_y = 1.0 - squash_progress * VfxJamSpillTask.SQUASH_FLATTEN_Y;

        blob.group.scale.set( scale_xz, scale_y, scale_xz );
      }
    }

    return false;
  }

  private spawnFloorSplash( pos: THREE.Vector3, floor_y: number, now: number ): void
  {
    const jam_batch = Vfx3DRuntime.getBatch( VfxPhysicsParticleBatch );
    const count = VfxJamSpillTask.SPLASH_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = randomRange( 0, Math.PI * 2 );
      const speed = randomRange( VfxJamSpillTask.SPLASH_RADIAL_MIN, VfxJamSpillTask.SPLASH_RADIAL_MAX );
      const vx = Math.cos( angle ) * speed;
      const vz = Math.sin( angle ) * speed;
      const vy = randomRange( VfxJamSpillTask.SPLASH_UP_MIN, VfxJamSpillTask.SPLASH_UP_MAX );

      VfxJamSpillTask.scratch_splash_pos.set(
        pos.x + randomRange( -0.05, 0.05 ),
        floor_y + 0.05,
        pos.z + randomRange( -0.05, 0.05 )
      );
      VfxJamSpillTask.scratch_splash_vel.set( vx, vy, vz );

      const scale = randomRange( VfxJamSpillTask.SPLASH_SCALE_MIN, VfxJamSpillTask.SPLASH_SCALE_MAX );
      const lifespan = randomRange( VfxJamSpillTask.SPLASH_LIFESPAN_MIN, VfxJamSpillTask.SPLASH_LIFESPAN_MAX );
      const initial_dissolve = randomRange(
          VfxJamSpillTask.JAM_INITIAL_DISSOLVE_MIN,
          VfxJamSpillTask.JAM_INITIAL_DISSOLVE_MAX
        );

      jam_batch.spawnParticle(
        this.surface_res_id,
        VfxJamSpillTask.SPLASH_NOISE_RES_ID,
        VfxJamSpillTask.scratch_splash_pos,
        VfxJamSpillTask.scratch_splash_vel,
        VfxJamSpillTask.SPLASH_GRAVITY,
        scale,
        now,
        lifespan,
        initial_dissolve,
        VfxJamSpillTask.SPLASH_NOISE_SCALE,
        undefined,
        undefined,
        undefined,
        VfxJamSpillTask.SPLASH_DISSOLVE_START,
        VfxJamSpillTask.SPLASH_DISSOLVE_SOFTNESS
      );
    }
  }

  private disposeAll(): void
  {
    for ( const blob of this.blobs )
    {
      if ( blob.group.parent )
      {
        SceneManager.world.remove( blob.group );
      }
      blob.material.dispose();
    }
    if ( this.geometry )
      this.geometry.dispose();
    this.blobs = [];
  }
}
